import { textactive2 } from "../_helpers/_parametric_tools";
import {
  axisBottom,
  axisRight,
  axisLeft,
  range,
  scaleBand,
  scaleLinear,
  scaleOrdinal,
  scaleTime,
  selectAll,
  timeFormat,
} from "d3";

export const ChartAxis = {
  setAxisDateLin({
    xRange,
    isXRange = false,
    data = xRange,
    minMaxYAxis,
    yAxisLeft = true,
    zeroXLine = true,
    customYTicks = false,
    yTickLabels = [],
    customXTicks = false,
    xTickLabels = [],
    zeroLineField = "zeroLine",
    xTickFormat,
  }) {
    let svg = this.svg;
    let svgAux = this.zoomChartBody;
    if (!svgAux) svgAux = svg;

    svg.x = scaleTime().range(
      !isXRange ? range(0, svg.width, svg.width / data.length) : [0, svg.width],
    );
    svg.y = scaleLinear().range([svg.height, 0]);

    svg.x.domain(xRange);
    svg.y.domain(minMaxYAxis);

    let tickCount = svg.width / 100;
    svg.xAxis = axisBottom(svg.x)
      .ticks(((svg.width + 2) / (svg.height + 2)) * 5)
      .tickSize(-svg.height)
      .tickPadding(10)
      .ticks(tickCount);

    let widthAux;
    tickCount = svg.height / 40;
    if (yAxisLeft) {
      svg.yAxis = axisLeft(svg.y)
        .tickSize(-svg.width)
        .tickSizeOuter(0)
        .tickPadding(10)
        .ticks(tickCount);
      widthAux = 0;
    } else {
      svg.yAxis = axisRight(svg.y)
        .tickSize(-svg.width)
        .tickSizeOuter(0)
        .tickPadding(10)
        .ticks(tickCount);
      widthAux = svg.width;
    }
    if (customYTicks) {
      svg.yAxis.tickValues(yTickLabels);
    }
    if (customXTicks) {
      svg.xAxis.tickValues(xTickLabels).tickFormat(timeFormat(xTickFormat));
    }

    svg.gX = svg
      .append("g")
      .attr("class", "axis axis--x")
      .attr("transform", "translate(0," + svg.height + ")")
      .call(svg.xAxis);
    svg.gY = svg
      .append("g")
      .attr("class", "axis axis--y")
      .attr("transform", "translate( " + widthAux + ", 0 )")
      .call(svg.yAxis);

    const gYWidth = svg.width + svg.margin.right + svg.margin.left;

    svg.gYRect = svg
      .append("g")
      .attr("class", "axis axis--y--rect")
      .attr("transform", "translate( " + gYWidth + ", 0 )")
      .append("rect")
      .attr("x", -gYWidth)
      .attr("y", 0)
      .attr("width", gYWidth)
      .attr("height", svg.height)
      .attr("fill-opacity", 0);

    // add a horizontal line at the max value of the yaxis
    svg
      .append("line")
      .attr("class", "xAxisLine")
      .attr("x1", 0)
      .attr("y1", 0)
      .attr("x2", svg.width)
      .attr("y2", 0);

    if (zeroXLine) {
      // add a horizontal line at y = 0
      svg[zeroLineField] = svgAux
        .append("line")
        .attr("class", "xAxisLine")
        .attr("x1", 0)
        .attr("y1", svg.y(0))
        .attr("x2", svg.width)
        .attr("y2", svg.y(0));
    }
    this.svg = svg;
  },
  setAxis({
    xData,
    minMaxYAxis,
    minMaxXAxis = [0, 1],
    xFormat,
    nXTicks = 60,
    nYTicks = 8,
    tickLabels = [],
    customTicks = false,
    showY = true,
    addYFormat = false,
    yFormat,
    yAxisLeft = true,
    showX = true,
    xIsLinear = false,
    isNice = true,
    zeroXLine = true,
    valueScaleBand = "date",
    svgX1 = false,
    keys = [],
    colorScale = [],
  }) {
    const svg = this.svg;
    const [minimal, maximal] = minMaxYAxis;
    if (xIsLinear) {
      svg.x = scaleLinear()
        .range([0, svg.width])
        .domain([minMaxXAxis[0], minMaxXAxis[1]]);
      if (isNice) {
        svg.x = scaleLinear()
          .range([0, svg.width])
          .domain([minMaxXAxis[0], minMaxXAxis[1]])
          .nice();
      }
    } else {
      svg.x = scaleBand()
        .range([0, svg.width])
        .domain(xData.map((d) => d[valueScaleBand]));

      if (svgX1) {
        svg.x1 = scaleBand()
          .domain(keys)
          .rangeRound([0, svg.x.bandwidth()]);

        svg.colorScale = scaleOrdinal().range(colorScale);
      }
    }

    if (isNice) {
      svg.y = scaleLinear()
        .range([svg.height, 0])
        .domain([minimal, maximal])
        .nice();
    } else {
      svg.y = scaleLinear()
        .range([svg.height, 0])
        .domain([minimal, maximal]);
    }
    let yAxis, widthAux;
    if (yAxisLeft) {
      yAxis = axisLeft(svg.y);
      widthAux = 0;
    } else {
      yAxis = axisRight(svg.y);
      widthAux = svg.width;
    }
    const getYAxis = ({ size, element = yAxis }) =>
      (svg.yAxis = element
        .tickSize(-svg[size])
        .tickSizeOuter(0)
        .tickPadding(10)
        .ticks(nYTicks));

    if (showY) {
      // add the y Axis
      if (customTicks) {
        if (addYFormat) {
          getYAxis({ size: "width" })
            .tickValues(tickLabels)
            .tickFormat(yFormat);
        } else {
          getYAxis({ size: "width" }).tickValues(tickLabels);
        }
      } else {
        if (addYFormat) {
          getYAxis({ size: "width" }).tickFormat(yFormat);
        } else {
          getYAxis({ size: "width" });
        }
      }
      svg.gY = svg
        .append("g")
        .attr("class", "axis axis--y")
        .attr("transform", "translate( " + widthAux + ", 0 )")
        .call(svg.yAxis);
    }
    const gXAxis = svg
      .append("g")
      .attr("class", "axis axis--x")
      .attr("transform", "translate(0," + svg.height + ")");
    // add the x Axis
    if (xIsLinear) {
      getYAxis({ size: "height", element: axisBottom(svg.x) });
      gXAxis.call(svg.xAxis);
    } else {
      const xtickdelta = Math.ceil(nXTicks / (svg.width / xData.length));
      if (showX) {
        gXAxis.call(
          axisBottom(svg.x)
            .tickValues(
              svg.x.domain().filter(function(d, i) {
                if (svgX1) {
                  return d;
                }
                return !((i + Math.floor(xtickdelta / 2)) % xtickdelta);
              }),
            )
            .tickFormat((d) => (svgX1 ? d : timeFormat(xFormat)(d))),
        );
      }
    }
    const gYWidth = svg.width + svg.margin.right + svg.margin.left;

    svg.gYRect = svg
      .append("g")
      .attr("class", "axis axis--y--rect")
      .attr("transform", "translate( " + gYWidth + ", 0 )")
      .append("rect")
      .attr("x", -gYWidth)
      .attr("y", 0)
      .attr("width", gYWidth)
      .attr("height", svg.height)
      .attr("fill-opacity", 0);

    this.addLines({ type: "lineGeneral" });
    if (
      Number.isFinite(minimal) &&
      Number.isFinite(maximal) &&
      Math.sign(minimal * maximal) < 0 &&
      zeroXLine
    ) {
      this.addLines({ type: "lineGeneral" });
    }
    this.svg = svg;
  },
  setDoubleAxisDateLin({
    xRange,
    minMaxYAxis,
    minMax2YAxis,
    color1,
    color2,
    invert2 = false,
  }) {
    const svg = this.svg;
    svg.x = scaleTime().range(range(0, svg.width, svg.width / xRange.length));
    svg.y = scaleLinear().range([svg.height, 0]);
    svg.y2 = scaleLinear().range([
      invert2 ? 0 : svg.height,
      invert2 ? svg.height : 0,
    ]);

    svg.x.domain(xRange);
    svg.y.domain(minMaxYAxis);
    svg.y2.domain(minMax2YAxis);

    let tickCount = svg.width / 100;
    svg.xAxis = axisBottom(svg.x)
      .ticks(((svg.width + 2) / (svg.height + 2)) * 5)
      .tickSize(-svg.height)
      .tickPadding(10)
      .ticks(tickCount);

    tickCount = svg.height / 40;
    svg.yAxis = axisLeft(svg.y)
      .tickSize(-svg.width)
      .tickSizeOuter(0)
      .tickPadding(10)
      .ticks(tickCount);
    svg.y2Axis = axisRight(svg.y2)
      .tickSize(0)
      .tickSizeOuter(0)
      .tickPadding(10)
      .ticks(tickCount);

    svg.gX = svg
      .append("g")
      .attr("class", "axis axis--x")
      .attr("transform", "translate(0," + svg.height + ")")
      .call(svg.xAxis);
    svg.gY = svg
      .append("g")
      .attr("class", "axis axis--y y-axis-1")
      .attr("transform", "translate( 0, 0 )")
      .call(svg.yAxis);
    selectAll(".y-axis-1 path").style("stroke", color1);
    selectAll(".y-axis-1 text").style("fill", color1);

    svg.gY2 = svg
      .append("g")
      .attr("class", "axis axis--y y-axis-2")
      .attr("transform", "translate( " + svg.width + ", 0 )")
      .call(svg.y2Axis);
    selectAll(".y-axis-2 path").style("stroke", color2);
    selectAll(".y-axis-2 text").style("fill", color2);

    this.addLines({
      type: "lineGeneral",
      strokeColor: textactive2(this.colorTheme),
    });
    this.svg = svg;
  },
};
